import { computed, ref } from 'vue';
import { useToast } from 'primevue/usetoast';
import { useStore } from 'vuex';
import { tryCatch } from '@/utils/tryCatch';
import { useRoute, useRouter } from 'vue-router';
import { STATUS_SALVAMENTO_ENUM } from '@/store/landingPage';
import { ComponenteFactory } from '@/factories/ComponenteFactory';

export function useEditorLandingPage() {
    const router = useRouter();
    const route = useRoute();
    const store = useStore();
    const toast = useToast();

    const NOME_MODULO_VUEX = 'landingPage';
    const janelaPreview = ref(null);

    const idLandingPage = computed(() => store.getters[`${NOME_MODULO_VUEX}/obterIdDaLandingPage`]);
    const nomeDoEvento = computed(() => store.getters[`${NOME_MODULO_VUEX}/obterNomeEvento`]);
    const secoesPagina = computed(() => store.state[NOME_MODULO_VUEX].estruturaPagina.children || []);
    const statusPreview = computed(() => store.getters[`${NOME_MODULO_VUEX}/obterStatusPreview`]);
    const statusAtualizacao = computed(() => store.getters[`${NOME_MODULO_VUEX}/obterStatusAtualizacao`]);
    const statusSalvamento = computed(() => store.getters[`${NOME_MODULO_VUEX}/obterStatusSalvamento`]);
    const statusCarregamentoEditorLandingPage = computed(() => store.getters[`${NOME_MODULO_VUEX}/obterStatusCarregamentoEditorLandingPage`]);
    const idElementoSelecionado = computed(() => store.getters[`${NOME_MODULO_VUEX}/obterIdElementoSelecionado`]);
    const dirtyState = computed(() => store.getters[`${NOME_MODULO_VUEX}/obterDirtyState`]);
    const temFormulario = computed(() => store.getters[`${NOME_MODULO_VUEX}/temFormulario`]);
    const botaoFormulario = computed(() => store.getters[`${NOME_MODULO_VUEX}/obterElementoPorComponente`]('botaoEnviar'));

    const elementoSelecionado = computed(() => store.getters[`${NOME_MODULO_VUEX}/obterElementoSelecionado`]);

    /**
     * Cria um novo componente usando a fábrica de componentes
     * @param {Object} modelo - Modelo do componente a ser criado
     * @returns {Object} Novo componente
     */
    function criarNovoComponente(modelo) {
        if (!modelo || !modelo.componente) {
            console.error('Modelo inválido para criar componente:', modelo);
            return null;
        }

        if (modelo.componenteId && modelo.props) {
            return {
                ...modelo,
                componenteId: modelo.componenteId,
                props: { ...modelo.props },
                children: modelo.children || [],
            };
        }

        return ComponenteFactory.criar(modelo.componente, modelo.props || {}, modelo.children || []);
    }

    const inicializarEstrutura = ({ eventoId, landingPageId }) => {
        store.dispatch('landingPage/inicializarEditor', {
            eventoId,
            landingPageId,
        });
    };

    const obterChildrenPorId = (id) => {
        return store.getters[`${NOME_MODULO_VUEX}/obterChildrenPorId`](id);
    };

    const atualizarItensSecao = (idSecao, novaListaItens) => {
        store.dispatch(`${NOME_MODULO_VUEX}/atualizarItensSecao`, {
            idSecao,
            novaListaItens,
        });
    };

    const removerComponente = (componenteId) => {
        store.dispatch(`${NOME_MODULO_VUEX}/removerComponente`, componenteId);
        if (componenteId === idElementoSelecionado.value) {
            selecionarElemento(null);
        }
    };

    const salvarPaginaAtual = async () => {
        const eventoId = route.params.eventoId;
        if (eventoId == null) return Promise.reject(new Error('ID do Evento não fornecido.'));
        if (typeof eventoId !== 'string') return Promise.reject(new Error('ID do Evento inválido.'));

        const resultado = await store.dispatch(`${NOME_MODULO_VUEX}/salvarLandingPage`, eventoId);

        if (resultado?.redirectTo) {
            toast.add({
                severity: 'warn',
                summary: 'Landing Page Duplicada',
                detail: 'Já existe uma landing page para este evento. Redirecionando para a página existente.',
                life: 5000,
            });

            router.push({
                name: 'Checkins_Eventos_EditarLanding',
                params: {
                    landingPageId: resultado.redirectTo,
                    eventoId,
                },
            });

            inicializarEstrutura({ eventoId, landingPageId: resultado.redirectTo });
            return;
        }

        if (store.getters[`${NOME_MODULO_VUEX}/obterStatusSalvamento`] === STATUS_SALVAMENTO_ENUM.ERRO) {
            toast.add({
                severity: 'error',
                summary: 'Erro Salvando Landing Page',
                detail: 'Ocorreu um erro ao salvar a landing page, tente novamente mais tarde.',
                life: 3000,
            });
            return;
        }

        if (route.params.landingPageId === idLandingPage.value) {
            return;
        }

        router.push({
            name: 'Checkins_Eventos_EditarLanding',
            params: {
                landingPageId: idLandingPage.value,
                eventoId,
            },
        });
    };

    const salvarSilenciosamente = async () => {
        const eventoId = route.params.eventoId;
        if (eventoId == null) return Promise.reject(new Error('ID do Evento não fornecido.'));
        if (typeof eventoId !== 'string') return Promise.reject(new Error('ID do Evento inválido.'));

        const resultado = await store.dispatch(`${NOME_MODULO_VUEX}/salvarLandingPage`, eventoId);

        if (resultado?.redirectTo) {
            inicializarEstrutura({ eventoId, landingPageId: resultado.redirectTo });
            return;
        }

        const statusSalvamento = store.getters[`${NOME_MODULO_VUEX}/obterStatusSalvamento`];
        if (statusSalvamento === STATUS_SALVAMENTO_ENUM.ERRO) {
            return Promise.reject(new Error('Erro ao salvar'));
        }

        return Promise.resolve();
    };

    // eslint-disable-next-line no-unused-vars
    const atualizarPaginaAtual = async (landingPageId) => {
        if (landingPageId == null) return Promise.reject(new Error('ID da Landing Page não fornecido.'));
        if (typeof eventoId !== 'string') return Promise.reject(new Error('ID da Landing Page inválido.'));

        // TODO: Implementar
    };

    const selecionarElemento = (componenteId) => {
        if (componenteId === null) {
            store.dispatch(`${NOME_MODULO_VUEX}/selecionarElemento`, null);
            return;
        }

        if (typeof componenteId !== 'string' || !componenteId) {
            console.error('[COMPOSABLE selecionarElemento] ID de elemento inválido recebido:', componenteId);
            return;
        }
        store.dispatch(`${NOME_MODULO_VUEX}/selecionarElemento`, componenteId);
    };

    const abrirPreview = ({ landingPageId }) => {
        return new Promise((resolve, reject) => {
            store.commit(`${NOME_MODULO_VUEX}/DEFINIR_STATUS_PREVIEW`, 'ABRINDO');

            // Fechar a janela anterior se existir
            if (janelaPreview.value && !janelaPreview.value.closed) {
                janelaPreview.value.close();
            }

            const url = `${window.RUNTIME_CONFIG?.LANDING_PAGE_PREVIEW_URL || process.env.VUE_APP_LANDING_PAGE_PREVIEW_URL}/preview/${landingPageId}`;
            const novaJanela = window.open(url, '_blank');

            if (novaJanela === null || novaJanela === undefined || novaJanela.closed) {
                store.dispatch(`${NOME_MODULO_VUEX}/_definirStatusTemporario`, {
                    mutation: 'DEFINIR_STATUS_PREVIEW',
                    status: 'ERRO',
                    duracao: 4000,
                });
                reject(new Error('Não foi possível abrir a janela de preview.'));
                return;
            }

            // Armazenar a referência localmente, não no store
            janelaPreview.value = novaJanela;

            let token = store.getters['auth/getToken'];

            // Se não encontrou o token, tenta obter do localStorage como fallback
            if (!token && localStorage.getItem('token')) {
                token = localStorage.getItem('token');
            }

            // Se ainda não encontrou o token, rejeita a promise
            if (!token) {
                console.error('Token de autenticação não encontrado');
                store.dispatch(`${NOME_MODULO_VUEX}/_definirStatusTemporario`, {
                    mutation: 'DEFINIR_STATUS_PREVIEW',
                    status: 'ERRO',
                    duracao: 4000,
                });
                reject(new Error('Token de autenticação não encontrado'));
                return;
            }

            const mensagem = { token };
            // Listener para aguardar que a janela de preview esteja pronta
            function handleReadyForToken(event) {
                if (event.origin !== `${window.RUNTIME_CONFIG?.LANDING_PAGE_PREVIEW_URL || process.env.VUE_APP_LANDING_PAGE_PREVIEW_URL}`) return;
                if (event.data && event.data.readyForToken) {
                    // Remove o listener de "pronto" quando receber a mensagem
                    window.removeEventListener('message', handleReadyForToken);

                    // Envia o token para a janela de preview
                    novaJanela.postMessage(mensagem, `${window.RUNTIME_CONFIG?.LANDING_PAGE_PREVIEW_URL || process.env.VUE_APP_LANDING_PAGE_PREVIEW_URL}`);

                    // Configura um listener para aguardar a confirmação
                    const confirmListener = (event) => {
                        if (event.origin !== `${window.RUNTIME_CONFIG?.LANDING_PAGE_PREVIEW_URL || process.env.VUE_APP_LANDING_PAGE_PREVIEW_URL}`) return;
                        if (event.data && event.data.confirmacao === 'tokenRecebido') {
                            window.removeEventListener('message', confirmListener);
                            // TODO: Ajustar status do preview para que ele fique aberto apenas enquantou houve janela de preview aberta
                            store.dispatch(`${NOME_MODULO_VUEX}/_definirStatusTemporario`, {
                                mutation: 'DEFINIR_STATUS_PREVIEW',
                                status: 'ABERTO',
                                duracao: 4000,
                            });
                            resolve();
                        }
                    };

                    window.addEventListener('message', confirmListener);

                    // Timeout para a confirmação
                    setTimeout(() => {
                        window.removeEventListener('message', confirmListener);
                        store.dispatch(`${NOME_MODULO_VUEX}/_definirStatusTemporario`, {
                            mutation: 'DEFINIR_STATUS_PREVIEW',
                            status: 'ERRO',
                            duracao: 4000,
                        });
                        reject(new Error('Timeout: Janela não confirmou o recebimento do token.'));
                    }, 1000 * 60 * 60 * 24);
                }
            }

            // Adiciona o listener para aguardar que a janela esteja pronta
            window.addEventListener('message', handleReadyForToken);

            // Timeout para aguardar que a janela esteja pronta
            setTimeout(() => {
                window.removeEventListener('message', handleReadyForToken);
                store.dispatch(`${NOME_MODULO_VUEX}/_definirStatusTemporario`, {
                    mutation: 'DEFINIR_STATUS_PREVIEW',
                    status: 'ERRO',
                    duracao: 4000,
                });
                reject(new Error('Timeout: Janela de preview não sinalizou que está pronta.'));
            }, 1000 * 60 * 60 * 24);
        });
    };

    async function carregarDadosEvento(eventoId) {
        if (!eventoId) return null;
        try {
            const nome = await store.dispatch(`${NOME_MODULO_VUEX}/carregarNomeEvento`, eventoId);
            return nome;
        } catch (error) {
            console.error('Erro ao carregar nome do evento:', error);
            return null;
        }
    }

    async function adicionarCampoPersonalizado() {
        const { componenteId } = store.getters[`${NOME_MODULO_VUEX}/formularioAtivo`];
        if (!componenteId) {
            toast.add({
                severity: 'error',
                summary: 'Formulário não encontrado',
                detail: 'Não foi possível adicionar campo personalizado: o formulário não foi encontrado na estrutura da landing page.',
                life: 3000,
            });
            return;
        }

        const novoItem = ComponenteFactory.criar('campoTexto');
        store.dispatch(`${NOME_MODULO_VUEX}/adicionarChildrenPorId`, {
            idComponente: componenteId,
            novoItem,
        });

        selecionarElemento(novoItem.componenteId);
        try {
            await salvarSilenciosamente();
        } catch (error) {
            console.error('Erro ao salvar campo personalizado:', error);
            toast.add({
                severity: 'warn',
                summary: 'Aviso de Salvamento',
                detail: 'O campo foi adicionado mas pode não ter sido salvo. Aguarde alguns segundos antes de navegar.',
                life: 4000,
            });
        }
    }

    const selecionarBotaoFormulario = () => {
        store.dispatch(`${NOME_MODULO_VUEX}/selecionarElementoPorComponente`, 'botaoEnviar');
    };

    const selecionarBotaoLimparCampos = () => {
        store.dispatch(`${NOME_MODULO_VUEX}/selecionarElemento`, 'botaoLimpar-proxy');
    };

    const limparCamposFormulario = () => {
        store.dispatch(`${NOME_MODULO_VUEX}/limparCamposFormulario`);
    };

    const publicarLandingPage = async () => {
        const landingPageId = route.params.landingPageId;
        if (!landingPageId) return Promise.reject(new Error('ID da landing page não fornecido.'));

        await salvarPaginaAtual();
        const { error } = await tryCatch(store.dispatch(`${NOME_MODULO_VUEX}/publicarLandingPage`));
        const urlPersonalizada = store.getters[`${NOME_MODULO_VUEX}/obterCampoMetadadoPorChave`]('urlPersonalizada');

        if (!urlPersonalizada) {
            toast.add({
                severity: 'error',
                summary: 'URL não encontrada',
                detail: 'A URL personalizada não foi definida para esta página.',
                life: 3000,
            });
            return;
        }

        if (error) {
            console.log(error);
            toast.add({
                severity: 'error',
                summary: 'Erro ao Publicar',
                detail: 'Ocorreu um erro ao publicar a landing page, tente novamente mais tarde.',
                life: 3000,
            });
        } else {
            toast.add({
                severity: 'success',
                summary: 'Landing Page Publicada',
                detail: 'Sua landing page foi publicada com sucesso!',
                life: 3000,
            });

            const baseUrl = window.RUNTIME_CONFIG?.LANDING_PAGE_PREVIEW_URL || process.env.VUE_APP_LANDING_PAGE_PREVIEW_URL;
            const url = `${baseUrl}/${urlPersonalizada}`;
            window.open(url, '_blank');
        }
    };

    return {
        idElementoSelecionado,
        elementoSelecionado,
        selecionarElemento,
        selecionarBotaoFormulario,
        botaoFormulario,
        selecionarBotaoLimparCampos,
        salvarPaginaAtual,
        inicializarEstrutura,
        abrirPreview,
        statusPreview,
        statusSalvamento,
        statusAtualizacao,
        statusCarregamentoEditorLandingPage,
        dirtyState,
        secoesPagina,
        obterChildrenPorId,
        atualizarItensSecao,
        carregarDadosEvento,
        nomeDoEvento,
        removerComponente,
        criarNovoComponente,
        idLandingPage,
        temFormulario,
        adicionarCampoPersonalizado,
        publicarLandingPage,
        limparCamposFormulario,
    };
}
